/**
 * Session-level circuit breaker for signaling retry management
 * Prevents retry storms when peers disconnect or sessions are invalid
 */

interface CircuitBreakerState {
  failureCount: number;
  lastFailureTime: number;
  circuitOpen: boolean;
  backoffUntil: number;
}

class SignalingCircuitBreaker {
  private static instance: SignalingCircuitBreaker;
  private peerStates = new Map<string, CircuitBreakerState>();
  
  // Circuit breaker thresholds
  private readonly MAX_FAILURES = 10; // After 10 legitimate retries, circuit break
  private readonly CIRCUIT_OPEN_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly FAILURE_WINDOW = 2 * 60 * 1000; // 2 minute failure window
  
  static getInstance(): SignalingCircuitBreaker {
    if (!SignalingCircuitBreaker.instance) {
      SignalingCircuitBreaker.instance = new SignalingCircuitBreaker();
    }
    return SignalingCircuitBreaker.instance;
  }
  
  /**
   * Check if we should attempt signaling to this peer
   */
  canAttemptSignaling(peerId: string): boolean {
    const state = this.peerStates.get(peerId);
    if (!state) return true;
    
    const now = Date.now();
    
    // If circuit is open, check if we can close it
    if (state.circuitOpen) {
      if (now > state.backoffUntil) {
        // Try to close circuit - reset to half-open state
        state.circuitOpen = false;
        state.failureCount = Math.floor(state.failureCount / 2);
        console.log(`[CIRCUIT-BREAKER] Half-opening circuit for peer ${peerId.slice(0, 8)}`);
        return true;
      }
      return false;
    }
    
    return true;
  }
  
  /**
   * Record a signaling failure for session-not-found errors
   */
  recordFailure(peerId: string, errorType: 'session-not-found' | 'other'): void {
    // Only track session-not-found failures for circuit breaking
    if (errorType !== 'session-not-found') return;
    
    const now = Date.now();
    let state = this.peerStates.get(peerId);
    
    if (!state) {
      state = {
        failureCount: 0,
        lastFailureTime: now,
        circuitOpen: false,
        backoffUntil: 0
      };
      this.peerStates.set(peerId, state);
    }
    
    // Reset failure count if outside failure window
    if (now - state.lastFailureTime > this.FAILURE_WINDOW) {
      state.failureCount = 0;
    }
    
    state.failureCount++;
    state.lastFailureTime = now;
    
    // Open circuit if too many failures
    if (state.failureCount >= this.MAX_FAILURES) {
      state.circuitOpen = true;
      state.backoffUntil = now + this.CIRCUIT_OPEN_DURATION;
      
      console.log(`[CIRCUIT-BREAKER] Opening circuit for peer ${peerId.slice(0, 8)} - ${state.failureCount} session-not-found failures`);
    }
  }
  
  /**
   * Record a successful signaling attempt
   */
  recordSuccess(peerId: string): void {
    const state = this.peerStates.get(peerId);
    if (state) {
      // Reset failure count on success
      state.failureCount = 0;
      state.circuitOpen = false;
      state.backoffUntil = 0;
      
      console.log(`[CIRCUIT-BREAKER] Resetting circuit for peer ${peerId.slice(0, 8)} after success`);
    }
  }
  
  /**
   * Mark a peer as disconnected - immediately open circuit
   */
  markPeerDisconnected(peerId: string): void {
    const state = this.peerStates.get(peerId) || {
      failureCount: this.MAX_FAILURES,
      lastFailureTime: Date.now(),
      circuitOpen: true,
      backoffUntil: Date.now() + this.CIRCUIT_OPEN_DURATION
    };
    
    state.circuitOpen = true;
    state.backoffUntil = Date.now() + this.CIRCUIT_OPEN_DURATION;
    this.peerStates.set(peerId, state);
    
    console.log(`[CIRCUIT-BREAKER] Marking peer ${peerId.slice(0, 8)} as disconnected - opening circuit`);
  }
  
  /**
   * Get current state for debugging
   */
  getState(peerId: string): CircuitBreakerState | null {
    return this.peerStates.get(peerId) || null;
  }
  
  /**
   * Clear all circuit breaker state (for testing)
   */
  reset(): void {
    this.peerStates.clear();
  }
  
  /**
   * Get stats for monitoring
   */
  getStats(): { totalPeers: number; openCircuits: number; recentFailures: number } {
    const now = Date.now();
    let openCircuits = 0;
    let recentFailures = 0;
    
    for (const state of this.peerStates.values()) {
      if (state.circuitOpen) openCircuits++;
      if (now - state.lastFailureTime < this.FAILURE_WINDOW) recentFailures++;
    }
    
    return {
      totalPeers: this.peerStates.size,
      openCircuits,
      recentFailures
    };
  }
}

export const signalingCircuitBreaker = SignalingCircuitBreaker.getInstance();
